/**
 * Admin Promo Code Management Page
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API, useLanguage } from "@/context/AppContext";
import { Gift, Users, Calendar, Loader2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const AdminPromoPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [promoStats, setPromoStats] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/admin/promo-stats`, { withCredentials: true });
        setPromoStats(res.data.promo_codes || []);
      } catch (error) {
        toast.error("Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="admin-promo-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin')} className="p-2" data-testid="back-to-admin-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{t('adminPagePromo')}</h1>
          <p className="text-gray-500 mt-1">Übersicht aller aktiven Promo-Codes</p>
        </div>
      </div>

      {promoStats.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <Gift className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Keine Promo-Codes vorhanden</p>
          </CardContent>
        </Card>
      ) : (
        promoStats.map((promo, index) => (
          <Card key={index} className="border-purple-200">
            <CardHeader className="bg-purple-50 border-b border-purple-100">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <Gift className="w-6 h-6 text-purple-600" />
                  <div>
                    <CardTitle className="text-lg font-mono">{promo.code}</CardTitle>
                    <p className="text-sm text-gray-500">{promo.description || "Promo-Code"}</p>
                  </div>
                </div>
                <Badge className={promo.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                  {promo.is_active ? "Aktiv" : "Inaktiv"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <Users className="w-4 h-4 mx-auto text-gray-500 mb-1" />
                  <p className="text-sm text-gray-500">Verwendet</p>
                  <p className="text-2xl font-bold text-[#0044CC]" data-testid="promo-used-count">
                    {promo.current_uses} / {promo.max_uses}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Verbleibend</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="promo-remaining-count">
                    {promo.remaining_uses}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Dauer</p>
                  <p className="text-2xl font-bold text-gray-700">{promo.duration_days} Tage</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <Calendar className="w-4 h-4 mx-auto text-gray-500 mb-1" />
                  <p className="text-sm text-gray-500">Läuft ab</p>
                  <p className="text-sm font-bold text-gray-700">
                    {promo.expires_at ? new Date(promo.expires_at).toLocaleDateString('de-CH') : "Kein Ablauf"}
                  </p>
                </div>
              </div>

              {promo.users && promo.users.length > 0 ? (
                <div>
                  <h4 className="font-semibold mb-3">Einlösungen ({promo.users.length})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3">Name</th>
                          <th className="text-left p-3">E-Mail</th>
                          <th className="text-left p-3">Eingelöst am</th>
                          <th className="text-left p-3">Gültig bis</th>
                        </tr>
                      </thead>
                      <tbody>
                        {promo.users.map((user, i) => (
                          <tr key={i} className="border-t" data-testid={`promo-user-${i}`}>
                            <td className="p-3">{user.user_name || "—"}</td>
                            <td className="p-3">{user.user_email || "—"}</td>
                            <td className="p-3">{user.start_date ? new Date(user.start_date).toLocaleDateString('de-CH') : "—"}</td>
                            <td className="p-3">{user.end_date ? new Date(user.end_date).toLocaleDateString('de-CH') : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                  Noch keine Einlösungen
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default AdminPromoPage;
